
import React from 'react';
import { BaseEdge, EdgeProps, getBezierPath, EdgeLabelRenderer, useReactFlow } from 'reactflow';
import { X } from 'lucide-react';
import { useEditorContext } from './EditorContext';

const CustomEdge: React.FC<EdgeProps> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  data,
}) => {
  const { setEdges } = useReactFlow();
  const { onRemoveEdge } = useEditorContext();

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const onEdgeClick = (evt: React.MouseEvent) => {
    evt.stopPropagation();
    if (onRemoveEdge) {
      onRemoveEdge(id);
    } else {
      // Fallback for independent usage
      setEdges((edges) => edges.filter((e) => e.id !== id));
    }
  };

  const isInCycle = data?.inCycle;

  return (
    <>
      <BaseEdge
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          ...style,
          stroke: isInCycle ? '#ef4444' : '#94a3b8',
          strokeWidth: isInCycle ? 3 : 2,
          transition: 'stroke 0.3s'
        }}
      />
      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            pointerEvents: 'all',
            zIndex: 10,
          }}
          className="nodrag nopan"
        >
          <button
            className="w-5 h-5 bg-white border border-slate-300 rounded-full flex items-center justify-center cursor-pointer hover:bg-red-50 hover:border-red-400 hover:text-red-500 text-slate-400 shadow-sm transition-all focus:outline-none"
            onClick={onEdgeClick}
            aria-label="Delete connection"
            title="Delete connection"
          >
            <X size={12} strokeWidth={2.5} />
          </button>
        </div>
      </EdgeLabelRenderer>
    </>
  );
};

// Memoized: with large graphs every editor state change would otherwise
// re-render all edge components (each carrying an EdgeLabelRenderer portal).
export default React.memo(CustomEdge);
